import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { format, parseISO, startOfWeek, subDays } from "date-fns";
import { Chart } from "primereact/chart";
import { Skeleton } from "primereact/skeleton";
import {
  BeakerIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import { KpiCard } from "../../components/dashboard/KpiCard";
import { useDashboardViewModel } from "../../viewmodels/dashboard/DashboardViewModel";
import type {
  ActivityRow,
  DashboardAuthorizedProps,
  DashboardSummary,
  ThroughputDailyRow,
} from "./dashboardTypes";
import "./dashboard.css";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

function number(value: unknown, maximumFractionDigits = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return parsed.toLocaleString(undefined, { maximumFractionDigits });
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function firstName() {
  const name = localStorage.getItem("name")?.trim();
  return name ? name.split(/\s+/)[0] : "there";
}

function StatusPill({ status }: { status?: string }) {
  const normalized = status?.toLowerCase() ?? "";
  const completed = normalized.includes("completed");
  const active = normalized.includes("process");
  const classes = completed
    ? "bg-emerald-50 text-emerald-700"
    : active
      ? "bg-lime-50 text-lime-700"
      : "bg-amber-50 text-amber-700";
  return (
    <span
      className={`rounded-md px-2 py-1 text-[11px] font-semibold ${classes}`}
    >
      {status || "Created"}
    </span>
  );
}

function ProductionOrders({ rows }: { rows: ActivityRow[] }) {
  return (
    <section className="dashboard-card flex min-h-[385px] flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4">
        <h2 className="text-lg font-semibold text-slate-950">
          Production Runs
        </h2>
        <Link
          to="/manufactured-products"
          className="text-sm font-semibold text-emerald-600 hover:text-emerald-700"
        >
          View all
        </Link>
      </div>
      <div className="flex-1 overflow-x-auto px-5">
        <table className="w-full min-w-[30rem] text-left text-sm">
          <thead className="border-b border-slate-200 text-xs text-slate-500">
            <tr>
              <th className="py-3 pr-4 font-medium">Order</th>
              <th className="px-4 py-3 font-medium">Item</th>
              <th className="py-3 pl-4 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.slice(0, 5).map((row) => (
              <tr key={String(row.id)} className="hover:bg-emerald-50/30">
                <td className="py-3 pr-4 font-medium text-slate-700">
                  <Link to={`/manufactured-products/${row.id}`}>#{row.id}</Link>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {row.product_name || row.lot_number || "—"}
                </td>
                <td className="py-3 pl-4">
                  <StatusPill status={row.status} />
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-5 py-10 text-center text-slate-400"
                >
                  No production runs in this period.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="p-4 pt-3">
        <Link
          to="/manufacture-product"
          className="flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-500 text-sm font-semibold text-white transition hover:bg-emerald-600"
        >
          <PlusIcon className="h-4 w-4" /> Create production
        </Link>
      </div>
    </section>
  );
}

function aggregateThroughput(rows: ThroughputDailyRow[]) {
  if (rows.length <= 14)
    return rows.map((row) => ({
      ...row,
      label: format(parseISO(row.date), "MMM d"),
    }));
  const weeks = new Map<string, ThroughputDailyRow & { label: string }>();
  rows.forEach((row) => {
    const start = startOfWeek(parseISO(row.date), { weekStartsOn: 1 });
    const key = format(start, "yyyy-MM-dd");
    const current = weeks.get(key) ?? {
      date: key,
      label: format(start, "MMM d"),
      production_kg: 0,
      shipped_kg: 0,
    };
    current.production_kg =
      Number(current.production_kg || 0) + Number(row.production_kg || 0);
    current.shipped_kg =
      Number(current.shipped_kg || 0) + Number(row.shipped_kg || 0);
    weeks.set(key, current);
  });
  return Array.from(weeks.values());
}

function ProductionShippingChart({ rows }: { rows: ThroughputDailyRow[] }) {
  const grouped = useMemo(() => aggregateThroughput(rows), [rows]);
  const data = useMemo(
    () => ({
      labels: grouped.map((row) => row.label),
      datasets: [
        {
          label: "Produced",
          data: grouped.map((row) => row.production_kg || 0),
          backgroundColor: "#22c55e",
          borderRadius: 5,
          barPercentage: 0.62,
        },
        {
          label: "Shipped",
          data: grouped.map((row) => row.shipped_kg || 0),
          backgroundColor: "#064e3b",
          borderRadius: 5,
          barPercentage: 0.62,
        },
      ],
    }),
    [grouped],
  );
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top" as const,
          align: "start" as const,
          labels: {
            usePointStyle: true,
            boxWidth: 7,
            color: "#64748b",
            font: { size: 10 },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: "#94a3b8", font: { size: 9 } },
        },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: "#f1f5f9" },
          ticks: { color: "#94a3b8", font: { size: 9 } },
        },
      },
    }),
    [],
  );
  return (
    <section className="dashboard-card min-h-[385px] p-5">
      <div className="flex items-start justify-between">
        <h2 className="text-lg font-semibold text-slate-950">
          Production & Shipping
        </h2>
        <span className="text-xs font-medium text-slate-500">
          Show by: {rows.length > 14 ? "Week" : "Day"}⌄
        </span>
      </div>
      <div className="mt-4 h-[295px]">
        {rows.length ? (
          <Chart type="bar" data={data} options={options} className="h-full" />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-slate-400">
            No throughput data.
          </div>
        )}
      </div>
    </section>
  );
}

function QuickStat({
  label,
  value,
  href,
  icon: Icon,
  tone,
}: {
  label: string;
  value?: number;
  href: string;
  icon: IconType;
  tone: string;
}) {
  return (
    <Link
      to={href}
      className="dashboard-card group flex min-h-[132px] flex-col justify-between p-5"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-700">{label}</p>
          <p className="mt-2 text-[1.75rem] font-semibold leading-none text-slate-950">
            {number(value)}
          </p>
        </div>
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <span className="text-sm font-medium text-emerald-600 group-hover:text-emerald-700">
        View details →
      </span>
    </Link>
  );
}

function DashboardContent({
  summary,
  activity,
  loading,
}: {
  summary: DashboardSummary | null;
  activity: ReturnType<typeof useDashboardViewModel>["activity"];
  loading: boolean;
}) {
  const overview = summary?.overview;
  const quick = summary?.quick_stats;
  const kpis = summary?.kpis;
  const productionRows =
    activity?.recent_productions ?? activity?.recent_production ?? [];
  return (
    <>
      <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {loading && !summary ? (
          Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} height="8.5rem" borderRadius="16px" />
          ))
        ) : (
          <>
            <KpiCard
              label="Weight Packaged"
              value={number(kpis?.packaging_volume?.weight_kg, 1)}
              suffix="kg"
              trend={kpis?.packaging_volume?.change_percent}
              icon={ArchiveBoxIcon}
              showTrend
            />
            <KpiCard
              label="Units Produced"
              value={number(overview?.units_produced)}
              trend={kpis?.manufactured_volume?.change_percent}
              icon={BeakerIcon}
              showTrend
            />
            <KpiCard
              label="Orders Shipped"
              value={number(overview?.orders_shipped)}
              trend={kpis?.shipped_sales?.change_percent}
              icon={TruckIcon}
              showTrend
            />
            <KpiCard
              label="On-Time Shipping"
              value={
                overview?.on_time_shipping_percent == null
                  ? "—"
                  : `${number(overview.on_time_shipping_percent, 1)}%`
              }
              icon={CheckCircleIcon}
              showTrend
            />
          </>
        )}
      </section>
      <section className="grid gap-5 xl:grid-cols-[0.9fr_1.65fr]">
        <ProductionOrders rows={productionRows} />
        <ProductionShippingChart rows={summary?.throughput_daily ?? []} />
      </section>
      <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <QuickStat
          label="Open Lots"
          value={quick?.open_lots}
          href="/inventory-manufactured-material"
          icon={CubeIcon}
          tone="bg-emerald-50 text-emerald-600"
        />
        <QuickStat
          label="Shipments"
          value={quick?.shipments}
          href="/shippings"
          icon={TruckIcon}
          tone="bg-sky-50 text-sky-600"
        />
        <QuickStat
          label="QA Holds"
          value={quick?.qa_holds}
          href="/credit-returns"
          icon={ExclamationTriangleIcon}
          tone="bg-rose-50 text-rose-600"
        />
        <QuickStat
          label="Low Inventory"
          value={quick?.low_inventory}
          href="/inventory-raw-material"
          icon={ArchiveBoxIcon}
          tone="bg-orange-50 text-orange-600"
        />
      </section>
    </>
  );
}

function DashboardAuthorized({ logout }: DashboardAuthorizedProps) {
  const { summary, activity, loading, fetchError, refresh } =
    useDashboardViewModel(logout);
  const fromDate = useMemo(
    () => format(subDays(new Date(), 29), "yyyy-MM-dd"),
    [],
  );
  const toDate = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  useEffect(() => {
    refresh(fromDate, toDate);
  }, [fromDate, toDate, refresh]);

  return (
    <main
      className="min-h-screen bg-[#f8faf8] p-4 text-slate-950 sm:p-6 lg:p-8"
      aria-busy={loading}
    >
      <div className="mx-auto max-w-[1500px] space-y-5">
        <header className="flex flex-col gap-4 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
              {greeting()}, {firstName()}
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              Here&apos;s what&apos;s happening in your operation.
            </p>
          </div>
          <Link
            to="/create-order"
            className="inline-flex h-11 w-fit items-center gap-2 rounded-lg bg-emerald-500 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600"
          >
            <PlusIcon className="h-5 w-5" /> New Order
          </Link>
        </header>
        {fetchError ? (
          <div
            role="alert"
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            {fetchError}
          </div>
        ) : null}
        <DashboardContent
          summary={summary}
          activity={activity}
          loading={loading}
        />
      </div>
    </main>
  );
}

export default function Dashboard(props: DashboardAuthorizedProps) {
  const userType = Number(localStorage.getItem("user_type"));
  return userType === 1 ? (
    <DashboardAuthorized {...props} />
  ) : (
    <main className="min-h-screen bg-[#f7faf7]" />
  );
}
